import { useEffect, useMemo, useRef, useState } from "react"
import { useField } from "formik"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { CreateOrderDialogMode, MeetingServicePriceListState, SelectedPriceListItem } from "../../../createOrderTypes"
import { CreateOrderType } from "../../../helpers"
import { CreateOrderCloseConfirmDialog } from "../../CreateOrderCloseConfirmDialog"

import { EmptyPriceListsDialog } from "./EmptyPriceListsDialog"
import {
    fetchMeetingServicePriceListsForUser,
    MeetingServicePriceListsQuery,
    MeetingServicePriceListsResponse,
} from "./priceListsApi"
import { SelectPriceListsDialog } from "./SelectPriceListsDialog"

type SelectPriceListsProps = Readonly<{
    userId: number
    onSelect: (item: SelectedPriceListItem) => void
    handleCloseMainDialog: () => void
    mode: CreateOrderDialogMode
    handleCloseSelectDialog: () => void
    orderType: CreateOrderType
    bookingOrderId?: number
}>

const transformData = (data: MeetingServicePriceListsResponse) => {
    return data.merchants
}

export function SelectPriceLists({
    userId,
    onSelect,
    handleCloseMainDialog,
    mode,
    handleCloseSelectDialog,
    orderType,
    bookingOrderId,
}: SelectPriceListsProps) {
    const [{ value: initPriceList }] = useField<MeetingServicePriceListState["item"]>("initPriceList")
    const [{ value: priceListState }] = useField<MeetingServicePriceListState>("priceList")
    const hasAutoSelectedRef = useRef(false)
    const [showConfirmModal, setShowConfirmModal] = useState(false)

    const { data: merchants = [], isLoading } = useApiQuery(fetchMeetingServicePriceListsForUser, {
        queryName: MeetingServicePriceListsQuery.USER_MEETING_SERVICE_PRICE_LISTS,
        dependencies: { userId, orderId: bookingOrderId },
        select: transformData,
        enabled: !!userId,
    })

    const filteredMerchants = useMemo(() => {
        if (mode === "create" || !initPriceList) return merchants
        const { merchantId, shopId } = initPriceList
        return merchants.filter((m) => m.id === merchantId && m.shop?.id === shopId)
    }, [merchants, mode, initPriceList?.merchantId, initPriceList?.shopId])

    useEffect(() => {
        if (isLoading) return
        if (hasAutoSelectedRef.current) return
        if (!filteredMerchants.length) return
        if (filteredMerchants.length !== 1) return
        if (filteredMerchants[0].priceLists.length !== 1) return

        const merchant = filteredMerchants[0]
        const onlyPriceList = merchant.priceLists[0]

        hasAutoSelectedRef.current = true
        onSelect({
            merchantId: merchant.id,
            merchantName: merchant.displayName,
            priceListId: onlyPriceList.id,
            priceListName: onlyPriceList.displayName,
            shopId: merchant.shop.id,
            isOnlyOnePriceList: true,
            priceListIncludeVat: onlyPriceList.includeVat,
        })
    }, [filteredMerchants, isLoading, onSelect])

    if (isLoading) return null

    if (!filteredMerchants.length) {
        return <EmptyPriceListsDialog onClose={handleCloseMainDialog} />
    }

    if (filteredMerchants.length === 1 && filteredMerchants[0].priceLists.length === 1) {
        return null
    }
    const onClose = () => {
        if (orderType === CreateOrderType.MEETING_SERVICE && !priceListState.item) {
            setShowConfirmModal(true)
        } else handleCloseSelectDialog()
    }

    return (
        <>
            <SelectPriceListsDialog
                onClose={onClose}
                merchants={filteredMerchants}
                onSelect={onSelect}
                userId={userId}
            />
            {showConfirmModal && (
                <CreateOrderCloseConfirmDialog
                    open={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    onDiscard={handleCloseMainDialog}
                />
            )}
        </>
    )
}
